/**
 * Generic helper to resolve button attribution for carousel items.
 * @typeParam T - Attribution result type (e.g. CardButtonAttribution)
 */

export type SingleButtonMatcher<T> = {
  match: (button: unknown) => boolean
  attribution: (button: unknown) => T
}

export type GetButtonAttributionConfig<T> = {
  singleMatchers: SingleButtonMatcher<T>[]
  getPerItemAttribution: (button: unknown, itemUuid: string) => T | null
  nullAttribution: T
}

/**
 * Resolves the button attribution for a given item in a carousel.
 * Tries single-button matchers first, then per-item attribution.
 */
export function getButtonAttribution<T>(
  button: unknown,
  itemUuid: string,
  config: GetButtonAttributionConfig<T>
): T {
  const { singleMatchers, getPerItemAttribution, nullAttribution } = config

  if (button != null) {
    const matcher = singleMatchers.find((m) => m.match(button))
    if (matcher) {
      return matcher.attribution(button)
    }
  }

  const perItem = getPerItemAttribution(button, itemUuid)
  if (perItem != null) {
    return perItem
  }

  return nullAttribution
}
